import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from "@mui/material";
import { fetchAnalysisGame, type AnalysisGame } from "./analysis.api";
import { canRunAnalysisPreset, PRESETS, type AnalysisPreset } from "./analysis-presets";
import { analysisRunner } from "./analysis-runner";
import { useAnalysisStore } from "./analysis.store";
import { AnalysisProgressPanel } from "./AnalysisProgressPanel";
import { FullEngineDownloadDialog } from "./FullEngineDownloadDialog";
import { QuickAnalysisWarningDialog } from "./QuickAnalysisWarningDialog";
import { useNotification } from "@/app/context/NotificationContext";
import { layoutTokens } from "@/shared/theme";
import { AnalysisPresetSelector } from "./components";
import { useNavigate } from "react-router-dom";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { resolveAnalysisAction } from "@/features/entitlements/entitlement-policy";

export interface CompletedSingleGameAnalysis {
  gameId: string;
  analysisDepth: number;
  result: {
    accuracy: number;
    averageCpl: number;
    blunderCount: number;
    mistakeCount: number;
  };
}

interface SingleGameAnalysisModalProps {
  gameId: string | null;
  onClose: () => void;
  onComplete: (completion: CompletedSingleGameAnalysis) => void | Promise<void>;
}

const ORIGINAL_DIALOG_TRANSITION = { enter: 140, exit: 120 } as const;

export const SingleGameAnalysisModal = ({ gameId, onClose, onComplete }: SingleGameAnalysisModalProps) => {
  const navigate = useNavigate();
  const entitlementSnapshot = useEntitlementsStore((state) => state.snapshot);
  const isRunning = useAnalysisStore((state) => state.isRunning);
  const fullEngineDownloaded = useAnalysisStore((state) => state.fullEngineDownloaded);
  const activeJob = useAnalysisStore((state) => (gameId ? state.jobs.find((job) => job.gameId === gameId) : undefined));
  const activeJobStatus = activeJob?.status;
  const { showNotification } = useNotification();
  const [game, setGame] = useState<AnalysisGame | null>(null);
  const analysisGate = resolveAnalysisAction(
    entitlementSnapshot,
    gameId ?? "",
    game?.existingAnalysisDepth != null,
    game?.exactTimeControl,
  );
  const [selected, setSelected] = useState<AnalysisPreset>(PRESETS[1]);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const [activeDialog, setActiveDialog] = useState<"analysis" | "quick" | "full" | null>("analysis");
  const [switchingDialogs, setSwitchingDialogs] = useState(false);
  const completionHandled = useRef(false);

  useLayoutEffect(() => {
    if (!gameId) return;
    setGame(null);
    setError(null);
    setSelected(PRESETS[1]);
    setStarted(false);
    setActiveDialog("analysis");
    setSwitchingDialogs(false);
    completionHandled.current = false;
  }, [gameId]);

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    fetchAnalysisGame(gameId)
      .then((loadedGame) => {
        if (cancelled) return;
        setGame(loadedGame);
        const nextPreset = PRESETS.find((preset) =>
          canRunAnalysisPreset(
            preset,
            loadedGame.existingAnalysisDepth,
            loadedGame.tacticalStatus === "needs_reanalysis",
          ),
        );
        if (nextPreset) setSelected(nextPreset);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load this game for analysis.");
      });
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  const startAnalysis = (preset = selected) => {
    if (!game || isRunning) return;
    if (!analysisGate.allowed) {
      onClose();
      if (analysisGate.cta)
        void navigate(analysisGate.cta === "manage_trial" ? "/subscription#trial" : "/subscription");
      return;
    }
    setSelected(preset);
    setStarted(true);
    void analysisRunner.start([game], preset);
  };
  const moveToDialog = (dialog: "analysis" | "quick" | "full", onSwitch?: () => void) => {
    setSwitchingDialogs(true);
    onSwitch?.();
    setActiveDialog(dialog);
  };
  const runAnalysis = () => {
    if (!analysisGate.allowed) {
      startAnalysis();
      return;
    }
    if (!selected.verifiesTacticalPatterns) {
      moveToDialog("quick");
      return;
    }
    if (selected.useFullEngine && !fullEngineDownloaded) {
      moveToDialog("full");
      return;
    }
    startAnalysis();
  };

  const existingDepth = game?.existingAnalysisDepth ?? null;
  const needsTacticalReanalysis = game?.tacticalStatus === "needs_reanalysis";
  const selectionIsStronger = canRunAnalysisPreset(selected, existingDepth, needsTacticalReanalysis);

  useEffect(() => {
    if (!started || completionHandled.current || activeJobStatus !== "done" || !activeJob?.result || !game) return;
    completionHandled.current = true;
    showNotification(`Analysis for ${game.opponent} complete!`);
    void Promise.resolve(
      onComplete({
        gameId: game.id,
        analysisDepth: selected.depth,
        result: {
          accuracy: activeJob.result.accuracy,
          averageCpl: activeJob.result.averageCpl,
          blunderCount: activeJob.result.blunderCount,
          mistakeCount: activeJob.result.mistakeCount,
        },
      }),
    ).finally(onClose);
  }, [activeJob?.result, activeJobStatus, game, onClose, onComplete, selected.depth, showNotification, started]);

  useEffect(() => {
    if (!started || completionHandled.current || activeJobStatus !== "error") return;
    completionHandled.current = true;
    showNotification(activeJob?.error ?? "Analysis could not finish. Try again.", "error");
    onClose();
  }, [activeJob?.error, activeJobStatus, onClose, showNotification, started]);

  return (
    <Fragment>
      <Dialog
        open={gameId !== null && activeDialog === "analysis"}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        keepMounted
        PaperProps={{
          sx: {
            width: { xs: "calc(100% - 16px)", sm: "calc(100% - 64px)" },
            maxWidth: 900,
            m: { xs: 1, sm: 4 },
            minWidth: 0,
            overflowX: "hidden",
          },
        }}
        transitionDuration={switchingDialogs ? 0 : ORIGINAL_DIALOG_TRANSITION}
        TransitionProps={{
          onEntered: () => setSwitchingDialogs(false),
        }}
      >
        <DialogTitle>
          {activeJobStatus === "done" && game
            ? `ANALYSIS COMPLETE: VS ${game.opponent.toUpperCase()}`
            : started && game
              ? `ANALYZING VS ${game.opponent.toUpperCase()}`
              : "CHOOSE ANALYSIS DEPTH"}
        </DialogTitle>
        <DialogContent sx={{ minWidth: 0, overflowX: "hidden", px: layoutTokens.pagePadding, pb: 0 }}>
          {!game && !error && <LinearProgress sx={{ my: 3 }} />}
          {error && (
            <Box sx={{ p: 1.5, border: "2px solid", borderColor: "secondary.main", color: "secondary.main" }}>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}
          {game && !started && (
            <Box>
              <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 800, letterSpacing: "0.1em" }}>
                GAME ANALYSIS / VS {game.opponent.toUpperCase()}
              </Typography>
              <Typography
                sx={{
                  color: "text.primary",
                  fontWeight: 800,
                  letterSpacing: "-0.04em",
                  fontSize: { xs: "1.7rem", sm: "2rem", md: "3.5rem" },
                  lineHeight: 1,
                  mt: 0.75,
                  mb: 1,
                }}
              >
                How deeply should
                <br />
                Nikola study this game?
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  maxWidth: 520,
                  fontSize: { xs: "0.72rem", sm: "0.875rem" },
                  mb: { xs: 1.5, sm: 3, md: 4 },
                }}
              >
                Pick the level of calculation you want for your game against {game.opponent}.
              </Typography>
              {existingDepth != null && existingDepth > 0 && (
                <Box
                  sx={{
                    borderLeft: "2px solid",
                    borderColor: "primary.main",
                    mb: { xs: 1.5, sm: 3, md: 4 },
                    pl: { xs: 1, sm: 1.5 },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontSize: { xs: "0.68rem", sm: "0.875rem" }, lineHeight: 1.4 }}
                  >
                    Existing {game.existingAnalysisPreset} analysis is depth {existingDepth}.
                    {needsTacticalReanalysis
                      ? " Re-run at the same or greater depth to verify tactical evidence."
                      : " Only a stronger analysis can replace it."}
                  </Typography>
                </Box>
              )}
              <AnalysisPresetSelector
                presets={PRESETS}
                preset={selected}
                onSelect={setSelected}
                showHeader={false}
                disabled={(preset) => !canRunAnalysisPreset(preset, existingDepth, needsTacticalReanalysis)}
              />
            </Box>
          )}
          {game && started && (
            <Box sx={{ minWidth: 0, pt: 1 }}>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
                Analyzing your game against {game.opponent} with {selected.label} at depth {selected.depth}.
              </Typography>
              <AnalysisProgressPanel gameId={game.id} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: layoutTokens.pagePadding, py: 2.5, flexWrap: "wrap" }}>
          <Button onClick={onClose}>{started ? "Close" : "Cancel"}</Button>
          {!started && (
            <Button
              variant="contained"
              onClick={runAnalysis}
              disabled={!game || isRunning || !selectionIsStronger || (!analysisGate.allowed && !analysisGate.cta)}
            >
              {isRunning
                ? "Analysis already running"
                : analysisGate.allowed
                  ? "Start analysis"
                  : analysisGate.cta === "manage_trial"
                    ? "Manage Trial"
                    : analysisGate.cta === "join_club"
                      ? "Join Club"
                      : "Checking…"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <FullEngineDownloadDialog
        open={gameId !== null && activeDialog === "full"}
        onClose={() => moveToDialog("analysis")}
        onConfirm={() => moveToDialog("analysis", () => startAnalysis())}
        onEntered={() => setSwitchingDialogs(false)}
        instantTransition={switchingDialogs}
      />
      <QuickAnalysisWarningDialog
        open={gameId !== null && activeDialog === "quick"}
        onClose={() => moveToDialog("analysis")}
        onContinueQuick={() => moveToDialog("analysis", () => startAnalysis())}
        onUseRegular={() => moveToDialog("analysis", () => startAnalysis(PRESETS[1]))}
        onEntered={() => setSwitchingDialogs(false)}
        instantTransition={switchingDialogs}
      />
    </Fragment>
  );
};
